import 'd3-transition';

import { isUtcDateAlignedToSp } from '@see-power-pft-common-js/utils/sp';
import {
  baseChartBuilder,
  ChartConfig,
  ChartContext,
  XDomain,
  XScale,
  YDomain,
  YScale,
} from 'components/Charting/ChartBuilder';
import { axesBlueprintBuilder } from 'components/Charting/Operations/axesBlueprint';
import { currentTimeLineBuilder } from 'components/Charting/Optimisation/currentTimeLine';
import { DisplayDateRange, SVGGSelection } from 'components/Charting/types';
import { isEmptyRecordOfArrays } from 'components/Charting/utils';
import { eachMinuteOfInterval } from 'date-fns';
import { dequal } from 'dequal';
import { debounce } from 'lodash';
import {
  AllTradeMarkets,
  AncillaryMarket,
  BalancingMechanismMarket,
  ColorMap,
  ContractedVolumeData,
  ContractedVolumeTooltipProps,
  StackTrade,
  WholesaleMarket,
} from 'pages/Optimisation/components/ContractedVolume/types';
import { AppThemeType } from 'store/types';
import { IChartTrade } from 'types/trade';
import { init } from 'utils/array';
import { getContractedPeriodTitle } from 'utils/optimisation';

interface OptimisationChartConfig extends ChartConfig {
  getFormattedYLabelValue: (value: number) => string;
}

export interface OptimisationChartDataContext {
  appTheme: AppThemeType;
  displayDateRange: DisplayDateRange;
  dataSubject: string;
  data: ContractedVolumeData;
  colorMap: ColorMap;
  setTooltipData: (data: ContractedVolumeTooltipProps | undefined) => void;
  errors: Array<string>;
  loading: boolean;
}

export interface OptimisationChartContext extends ChartContext<OptimisationChartDataContext> {
  config: OptimisationChartConfig;

  barsG: SVGGSelection;

  getFormattedYLabelValue: (value: number) => string;
}

const isValidChartTrade = (it: IChartTrade): boolean => {
  // filter out trades with a zero time window
  if (+it.start === +it.end) {
    return false;
  }

  if (+it.start > +it.end) {
    return false;
  }

  return true;
};

const ancillaryMarketDisplayOrders: AncillaryMarket[] = [
  'dcLow',
  'dcHigh',
  'dmLow',
  'dmHigh',
  'drLow',
  'drHigh',
  'positiveQr',
  'negativeQr',
  'positiveBr',
  'negativeBr',
  'positiveSr',
  'negativeSr',
  'positiveQrUtilisation',
  'negativeQrUtilisation',
  'positiveSrUtilisation',
  'negativeSrUtilisation',
];

const wholesaleMarketDisplayOrders: WholesaleMarket[] = [
  'otc',
  'longTermHedge',
  'dayAheadHalfHourly',
  'dayAheadHourly',
  'intraday',
  'systemPrice',
  'ida1',
  'ida2',
];

const balancingMechanismMarketDisplayOrders: BalancingMechanismMarket[] = [
  'balancingMechanismBid',
  'balancingMechanismOffer',
];

const marketDisplayOrders: Map<string, number> = new Map(
  [
    ...ancillaryMarketDisplayOrders,
    ...wholesaleMarketDisplayOrders,
    ...balancingMechanismMarketDisplayOrders,
  ].map((it, index) => [it, index]),
);

const getMarketDisplayOrder = (
  market: IChartTrade['market'],
  isSpAligned: boolean,
) => {
  const displayOrder =
    marketDisplayOrders.get(market) ??
    (() => {
      throw new Error(`No display order found for market ${market}`);
    })();

  const displayOrderBumpForNonSpAlignedTrades = isSpAligned
    ? 0
    : marketDisplayOrders.size;

  return displayOrder + displayOrderBumpForNonSpAlignedTrades;
};

const chartTradePreStackingSortComparer = (a: IChartTrade, b: IChartTrade) => {
  if (a.start < b.start) {
    return -1;
  }
  if (a.start > b.start) {
    return 1;
  }

  const isASpAligned =
    isUtcDateAlignedToSp(new Date(+a.start)) &&
    isUtcDateAlignedToSp(new Date(+a.end));

  const isBSpAligned =
    isUtcDateAlignedToSp(new Date(+b.start)) &&
    isUtcDateAlignedToSp(new Date(+b.end));

  if (
    getMarketDisplayOrder(a.market, isASpAligned) <
    getMarketDisplayOrder(b.market, isBSpAligned)
  ) {
    return -1;
  }
  if (
    getMarketDisplayOrder(a.market, isASpAligned) >
    getMarketDisplayOrder(b.market, isBSpAligned)
  ) {
    return 1;
  }

  const aTimespan = Math.abs(+a.end - +a.start);
  const bTimespan = Math.abs(+b.end - +b.start);

  if (aTimespan < bTimespan) {
    return a.value > 0 && b.value > 0 ? 1 : -1;
  }
  if (aTimespan > bTimespan) {
    return a.value > 0 && b.value > 0 ? -1 : 1;
  }

  // sort in order of value
  // desc for positive values, asc for negatives
  if (a.value < b.value) {
    return a.value > 0 && b.value > 0 ? 1 : -1;
  }
  if (a.value > b.value) {
    return a.value > 0 && b.value > 0 ? -1 : 1;
  }

  return 0;
};

type DateAsNumber = number;

const getStackTrades = (chartTrades: IChartTrade[]) =>
  chartTrades.toSorted(chartTradePreStackingSortComparer).reduce(
    (acc, it) => {
      const minuteIntervalsAsNumbers: DateAsNumber[] = init(
        eachMinuteOfInterval({
          start: it.start,
          end: it.end,
        }),
      ).map(Number);

      const offset =
        it.value > 0
          ? Math.max(
              ...minuteIntervalsAsNumbers.map(
                (interval) => acc.maximumOffsets.get(interval) ?? 0,
              ),
            )
          : Math.min(
              ...minuteIntervalsAsNumbers.map(
                (interval) => acc.minimumOffsets.get(interval) ?? 0,
              ),
            );

      return {
        stackTrades: [
          ...acc.stackTrades,
          { ...it, offset } as unknown as StackTrade,
        ],

        maximumOffsets:
          it.value > 0
            ? minuteIntervalsAsNumbers.reduce(
                (maximumOffsetsAcc, interval) =>
                  maximumOffsetsAcc.set(
                    interval,
                    (maximumOffsetsAcc.get(interval) ?? 0) + it.value,
                  ),
                acc.maximumOffsets,
              )
            : acc.maximumOffsets,

        minimumOffsets:
          it.value > 0
            ? acc.minimumOffsets
            : minuteIntervalsAsNumbers.reduce(
                (minimumOffsetsAcc, interval) =>
                  minimumOffsetsAcc.set(
                    interval,
                    (minimumOffsetsAcc.get(interval) ?? 0) + it.value,
                  ),
                acc.minimumOffsets,
              ),
      };
    },
    {
      stackTrades: [] as StackTrade[],
      maximumOffsets: new Map<DateAsNumber, number>(),
      minimumOffsets: new Map<DateAsNumber, number>(),
    },
  );

const getNonSpAlignedTradesSlicedForDisplay = (
  spAlignedTimestamps: Set<number>,
  nonSpAlignedTrades: IChartTrade[],
) => {
  const startAndEndDatesAsNumbers: DateAsNumber[] = Array.from(
    new Set([
      ...nonSpAlignedTrades.flatMap((it) => [+it.start, +it.end]),
      ...Array.from(spAlignedTimestamps),
    ]),
  ).toSorted((a, b) => a - b);

  const tradesSlicedAlongStartAndEndDates = nonSpAlignedTrades.flatMap(
    (trade) => {
      const indexOfStartDateAsNumber = startAndEndDatesAsNumbers.indexOf(
        +trade.start,
      );
      const indexOfEndDateAsNumber = startAndEndDatesAsNumbers.indexOf(
        +trade.end,
      );

      return [
        ...startAndEndDatesAsNumbers.slice(
          indexOfStartDateAsNumber,
          indexOfEndDateAsNumber,
        ),
        startAndEndDatesAsNumbers[indexOfEndDateAsNumber],
      ]
        .map((_, index, array) => {
          if (index === array.length - 1) {
            return undefined;
          }

          return {
            ...trade,
            start: new Date(array[index]),
            end: new Date(array[index + 1]),
            base: {
              start: new Date(array[index]),
              end: new Date(array[index + 1]),
            },
          };
        })
        .filter(Boolean) as IChartTrade[];
    },
  );

  const stackedSlicedTradesByTradeGroupSourceId = getStackTrades(
    tradesSlicedAlongStartAndEndDates,
  ).stackTrades.reduce(
    (acc, it) =>
      acc.set(it.tradeGroupSourceId, [
        ...(acc.get(it.tradeGroupSourceId) ?? []),
        it,
      ]),
    new Map<StackTrade['tradeGroupSourceId'], StackTrade[]>(),
  );

  return Array.from(stackedSlicedTradesByTradeGroupSourceId.values())
    .flatMap((group) => {
      const contiguousGroups = group.map((it) => [it]);

      const msIn30Minutes = 1000 * 60 * 30;

      while (true) {
        const contigiousGroupsToMerge: [StackTrade[], StackTrade[]][] = [];
        for (const contiguousGroupToMerge of contiguousGroups) {
          for (const contiguousGroupToMergeTo of contiguousGroups.filter(
            (it) => it !== contiguousGroupToMerge,
          )) {
            contigiousGroupsToMerge.push([
              contiguousGroupToMerge,
              contiguousGroupToMergeTo,
            ]);
          }
        }

        const contiguousGroupsToMergeToEnd:
          [StackTrade[], StackTrade[]] | undefined = (() => {
          for (const [
            contiguousGroupToMerge,
            contiguousGroupToMergeToEndOf,
          ] of contigiousGroupsToMerge) {
            const contiguousGroupToMergeStart =
              +contiguousGroupToMerge[0].start;
            const contiguousGroupToMergeToEndOfEnd =
              +contiguousGroupToMergeToEndOf[
                contiguousGroupToMergeToEndOf.length - 1
              ].end;

            if (
              contiguousGroupToMerge[0].offset ===
                contiguousGroupToMergeToEndOf[0].offset &&
              contiguousGroupToMergeStart % msIn30Minutes !== 0 &&
              contiguousGroupToMergeStart === contiguousGroupToMergeToEndOfEnd
            ) {
              return [contiguousGroupToMerge, contiguousGroupToMergeToEndOf];
            }
          }

          return;
        })();
        if (contiguousGroupsToMergeToEnd) {
          const [contiguousGroupToMerge, contiguousGroupToMergeToEndOf] =
            contiguousGroupsToMergeToEnd;

          contiguousGroupToMergeToEndOf.push(...contiguousGroupToMerge);

          contiguousGroups.splice(
            contiguousGroups.indexOf(contiguousGroupToMerge),
            1,
          );

          continue;
        }

        const contiguousGroupsToMergeToStart:
          [StackTrade[], StackTrade[]] | undefined = (() => {
          for (const [
            contiguousGroupToMerge,
            contiguousGroupToMergeToStartOf,
          ] of contigiousGroupsToMerge) {
            const contiguousGroupToMergeEnd =
              +contiguousGroupToMerge[contiguousGroupToMerge.length - 1].end;
            const contiguousGroupToMergeToStartOfStart =
              +contiguousGroupToMergeToStartOf[0].start;

            if (
              contiguousGroupToMerge[0].offset ===
                contiguousGroupToMergeToStartOf[0].offset &&
              contiguousGroupToMergeEnd % msIn30Minutes !== 0 &&
              contiguousGroupToMergeEnd === contiguousGroupToMergeToStartOfStart
            ) {
              return [contiguousGroupToMerge, contiguousGroupToMergeToStartOf];
            }
          }

          return;
        })();
        if (contiguousGroupsToMergeToStart) {
          const [contiguousGroupToMerge, contiguousGroupToMergeToStartOf] =
            contiguousGroupsToMergeToStart;

          contiguousGroupToMergeToStartOf.unshift(...contiguousGroupToMerge);

          contiguousGroups.splice(
            contiguousGroups.indexOf(contiguousGroupToMerge),
            1,
          );

          continue;
        }

        break;
      }

      return contiguousGroups;
    })
    .map((contiguousGroup) => {
      const start = contiguousGroup[0].start;
      const end = contiguousGroup[contiguousGroup.length - 1].end;

      return {
        ...contiguousGroup[0],
        start,
        end,
        base: { start, end },
      };
    }) as IChartTrade[];
};

// assumes data start/end are aligned to minute intervals
const getStackTradesAndYDomain = (
  data: OptimisationChartDataContext['data'],
): { stackTrades: StackTrade[]; yDomain: YDomain } => {
  const validTrades = Object.values(data).flat().filter(isValidChartTrade);

  const { spAlignedTrades, nonSpAlignedTrades, spAlignedTimestamps } =
    validTrades.reduce(
      (acc, it) => {
        const startTimestamp = +it.start;
        const endTimestamp = +it.end;

        const isStartSpAligned = isUtcDateAlignedToSp(new Date(startTimestamp));
        const isEndSpAligned = isUtcDateAlignedToSp(new Date(endTimestamp));

        if (isStartSpAligned && isEndSpAligned) {
          acc.spAlignedTrades.push(it);
        } else {
          acc.nonSpAlignedTrades.push(it);
        }

        if (isStartSpAligned) {
          acc.spAlignedTimestamps.add(startTimestamp);
        }

        if (isEndSpAligned) {
          acc.spAlignedTimestamps.add(endTimestamp);
        }

        return acc;
      },
      {
        spAlignedTrades: [] as IChartTrade[],
        nonSpAlignedTrades: [] as IChartTrade[],
        spAlignedTimestamps: new Set<number>(),
      },
    );

  const nonSpAlignedTradesSlicedForDisplay =
    getNonSpAlignedTradesSlicedForDisplay(
      spAlignedTimestamps,
      nonSpAlignedTrades,
    );

  const tradesSlicedForDisplay = [
    ...spAlignedTrades,
    ...nonSpAlignedTradesSlicedForDisplay,
  ];

  const { stackTrades, maximumOffsets, minimumOffsets } = getStackTrades(
    tradesSlicedForDisplay,
  );

  const minY = Math.min(0, ...Array.from(minimumOffsets.values()));
  const maxY = Math.max(0, ...Array.from(maximumOffsets.values()));

  const yDomain = [minY, maxY];

  return { stackTrades, yDomain };
};

const getYDomain = ({ data }: Pick<OptimisationChartDataContext, 'data'>) =>
  getStackTradesAndYDomain(data).yDomain;

const getXDomain = ({
  displayDateRange,
}: {
  displayDateRange: DisplayDateRange;
}) => [displayDateRange.start, displayDateRange.end];

const updateData = ({
  isReinitialising,
  context: { barsG },
  dataContext: { data, colorMap, setTooltipData },
  xDomain,
  xScale,
  yScale,
}: {
  isReinitialising: boolean;
  context: Pick<OptimisationChartContext, 'config' | 'barsG'>;
  dataContext: Pick<
    OptimisationChartDataContext,
    'data' | 'colorMap' | 'setTooltipData'
  >;
  xDomain: XDomain;
  xScale: XScale;
  yScale: YScale;
}) => {
  const getY = (d: StackTrade) =>
    yScale(d.offset + (d.value > 0 ? d.value : 0));

  const getWidth = (d: StackTrade) => {
    const widthOfBar = xScale(d.end) - xScale(d.start);

    const xGap = 1;

    return widthOfBar > xGap * 2 ? widthOfBar - xGap : widthOfBar;
  };

  const getHeight = ({ value }: StackTrade) => {
    const absHeightOfBarToMidLine = yScale(Math.abs(value));

    const yGap = 1;

    const absHeightOfBarToMidLineWithGap =
      absHeightOfBarToMidLine > yGap * 2
        ? absHeightOfBarToMidLine + yGap
        : absHeightOfBarToMidLine;

    const heightToMidLine = yScale(0);

    const heightOfBar = heightToMidLine - absHeightOfBarToMidLineWithGap;

    // limit to 0, for animateToStack zero data, where gaps may make zeroed values negative
    return Math.max(0, heightOfBar);
  };

  const determineFillColor = (trade: StackTrade): string => {
    const { key, market } = trade;
    if (!Object.keys(colorMap).includes(key)) {
      throw Error(`No color map found for ${key}`);
    }

    return colorMap[key][market as AllTradeMarkets] ?? '';
  };

  const toggleSplitBarsHover = (
    { base, market, id }: StackTrade,
    isHovered: boolean,
  ) => {
    if (!base) {
      return;
    }

    barsG.selectAll(`#${market}-${id}`).classed('hovered-bar', isHovered);
  };

  const onMouseover = debounce((_: any, stackTrade: StackTrade) => {
    toggleSplitBarsHover(stackTrade, true);

    setTooltipData({
      data: stackTrade,
      contractedPeriodTitle: getContractedPeriodTitle(
        stackTrade.key,
        stackTrade.start,
        xDomain[0],
        xDomain[1],
        stackTrade.market as AllTradeMarkets,
      ),
      color: determineFillColor(stackTrade),
    });
  }, 10);

  const onMouseout = debounce((_: any, stackTrade: StackTrade) => {
    toggleSplitBarsHover(stackTrade, false);
    setTooltipData(undefined);
  }, 10);

  const { stackTrades } = getStackTradesAndYDomain(data);

  const { initialStack, animateToStack } = isReinitialising
    ? (() => {
        const flattenedStack = stackTrades.map((it) => ({
          ...it,
          offset: 0,
          value: 0,
        })) as StackTrade[];

        return { initialStack: flattenedStack, animateToStack: stackTrades };
      })()
    : { initialStack: stackTrades, animateToStack: undefined };

  barsG
    .selectAll('.bar')
    .data(initialStack)
    .join(
      (enter) =>
        enter
          .append('rect')
          .classed('bar', true)
          .attr('id', (d) => `${d.market}-${d.id}`)
          .attr('data-key', (d) => d.key)
          .attr('x', (d) => xScale(d.start))
          .attr('y', getY)
          .attr('width', getWidth)
          .attr('height', getHeight)
          .attr('fill', (d) => determineFillColor(d))
          .attr('rx', '2px')
          .on('mouseover', onMouseover)
          .on('mouseout', onMouseout),
      (update) =>
        update
          .attr('x', (d) => xScale(d.start))
          .attr('y', getY)
          .attr('width', getWidth)
          .attr('height', getHeight)
          .attr('fill', determineFillColor)
          .on('mouseover', onMouseover)
          .on('mouseout', onMouseout),
      (exit) => exit.remove(),
    );

  if (animateToStack) {
    barsG
      .selectAll('.bar')
      .data(animateToStack)
      .attr('y', getY)
      .attr('x', (d) => xScale(d.start))
      .attr('fill', determineFillColor)
      .attr('width', getWidth)
      .attr('height', getHeight);
  }
};

const updateForNoData = ({
  context: { barsG },
}: {
  context: Pick<OptimisationChartContext, 'barsG'>;
}) => {
  barsG.selectAll('.bar').data([]).exit().remove();
};

/**
 * Builds an optimisation chart.
 *
 * @param {OptimisationChartContext['config']} config - The configuration for the optimisation chart.
 * @param {HTMLDivElement} chartContainerElement - The chart container element where the chart will be rendered.
 * @returns {OptimisationChartContext} The optimisation chart context.
 */
export const optimisationChartBuilder = (
  config: OptimisationChartContext['config'],
  chartContainerElement: HTMLDivElement,
): OptimisationChartContext => {
  const baseContext = baseChartBuilder(config, chartContainerElement);

  const barsG = baseContext.chartG.append('g').classed('bars', true);

  const context: OptimisationChartContext = {
    ...baseContext,

    config,

    barsG,

    getFormattedYLabelValue: config.getFormattedYLabelValue,
  };

  const {
    chartG,
    xAxisGridLinesG,
    yAxisGridLinesG,
    xAxisBottomG,
    yAxisLeftG,
    getFormattedYLabelValue,
  } = context;

  const { update: updateAxesBlueprint } = axesBlueprintBuilder({
    xAxisGridLinesG,
    yAxisGridLinesG,
    xAxisBottomG,
    yAxisLeftG,
    getFormattedYLabelValue,
  });

  const { update: updateCurrentTimeLine } = currentTimeLineBuilder();

  const update = (() => {
    let prevDataContext: OptimisationChartDataContext | undefined = undefined;
    let shouldInitialise = false;

    return (
      dataContext: OptimisationChartDataContext | undefined,
      forceRedraw = false,
    ) => {
      if (dequal(prevDataContext, dataContext) && !forceRedraw) {
        return;
      }
      const isChangingDataSubject =
        prevDataContext?.dataSubject !== dataContext?.dataSubject;

      const isChangingDisplayDateRange =
        +(prevDataContext?.displayDateRange?.start ?? 0) !==
        +(dataContext?.displayDateRange?.start ?? 0);

      if (isChangingDataSubject || isChangingDisplayDateRange) {
        shouldInitialise = true;
      }

      prevDataContext = dataContext;

      if (!dataContext) {
        return;
      }

      const { data } = dataContext;

      const isEmptyData = isEmptyRecordOfArrays(data);
      const isReinitialising = shouldInitialise && !isEmptyData;

      if (isReinitialising) {
        shouldInitialise = false;
      }

      const { appTheme, displayDateRange, colorMap, setTooltipData } =
        dataContext;

      const { innerWidth, innerHeight } = context.getInnerDimensions(context);

      const xDomain = getXDomain({ displayDateRange });

      const xScale = context.getXScale({ innerWidth, xDomain });

      updateCurrentTimeLine({
        appTheme,
        chartG,
        innerHeight,
        xScale,
      });

      const yUnitLabel = 'MW';

      const yDomain = getYDomain({ data });

      const yScale = context.getYScale({ innerHeight, yDomain });

      if (isEmptyData) {
        updateForNoData({ context: { barsG } });

        updateAxesBlueprint({
          innerWidth,
          innerHeight,
          xDomain,
          yDomain,
          xScale,
          yScale,
          yUnitLabel,
        });
        return;
      }

      updateAxesBlueprint({
        innerWidth,
        innerHeight,
        xDomain,
        yDomain,
        xScale,
        yScale,
        yUnitLabel,
      });

      updateData({
        isReinitialising,
        context: { config, barsG },
        dataContext: { data, colorMap, setTooltipData },
        xDomain,
        xScale,
        yScale,
      });
    };
  })();

  const onDestroy = () => {};

  return { ...context, config, update, destroy: onDestroy };
};
